// Admin API Service

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Get token from localStorage
const getToken = () => localStorage.getItem('admin_token');
const getUser = () => {
  const user = localStorage.getItem('admin_user');
  return user ? JSON.parse(user) : null;
};

// Set token and user
const setAuth = (token, user) => {
  localStorage.setItem('admin_token', token);
  localStorage.setItem('admin_user', JSON.stringify(user));
};

// Clear auth
const clearAuth = () => {
  localStorage.removeItem('admin_token');
  localStorage.removeItem('admin_user');
};

// Auth headers
const authHeaders = () => ({
  'Authorization': `Bearer ${getToken()}`,
  'Content-Type': 'application/json'
});

// ========================================
// AUTHENTICATION
// ========================================

export const login = async (email, password) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password })
    });

    if (!response.ok) {
      let errorMessage = 'Login failed';
      try {
        const error = await response.json();
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        // If response is not JSON, use default message
      }
      throw new Error(errorMessage);
    }

    const data = await response.json();
    setAuth(data.access_token, data.user);
    return { success: true, data };
  } catch (error) {
    console.error('Login error:', error);
    return { success: false, message: error.message };
  }
};

export const register = async (email, password, full_name) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, full_name })
    });

    if (!response.ok) {
      let errorMessage = 'Registration failed';
      try {
        const error = await response.json();
        errorMessage = error.detail || errorMessage;
      } catch (e) {
        // If response is not JSON, use default message
      }
      throw new Error(errorMessage);
    }

    return { success: true };
  } catch (error) {
    console.error('Registration error:', error);
    return { success: false, message: error.message };
  }
};

export const logout = () => {
  clearAuth();
};

export const isAuthenticated = () => !!getToken();
export const getCurrentUser = getUser;

// ========================================
// STATS
// ========================================

export const getStats = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/stats`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch stats');
    }
    return await response.json();
  } catch (error) {
    console.error('Stats error:', error);
    throw error;
  }
};

// ========================================
// QUOTES
// ========================================

export const getQuotes = async (statusFilter = null) => {
  try {
    const url = statusFilter 
      ? `${API_URL}/api/admin/quotes?status_filter=${statusFilter}`
      : `${API_URL}/api/admin/quotes`;
    
    const response = await fetch(url, { headers: authHeaders() });
    if (!response.ok) {
      throw new Error('Failed to fetch quotes');
    }
    return await response.json();
  } catch (error) {
    console.error('Quotes error:', error);
    throw error;
  }
};

export const updateQuoteStatus = async (quoteId, newStatus) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}/status?new_status=${newStatus}`, {
      method: 'PATCH',
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to update status');
    }
    return await response.json();
  } catch (error) {
    console.error('Update status error:', error);
    throw error;
  }
};

export const deleteQuote = async (quoteId) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/quotes/${quoteId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete quote');
    }
    return await response.json();
  } catch (error) {
    console.error('Delete quote error:', error);
    throw error;
  }
};

// ========================================
// GALLERY
// ========================================

export const getGalleryImages = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/gallery`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch gallery');
    }
    return await response.json();
  } catch (error) {
    console.error('Gallery error:', error);
    throw error;
  }
};

export const addGalleryImage = async (url, title, category) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/gallery`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ url, title, category })
    });
    if (!response.ok) {
      throw new Error('Failed to add image');
    }
    return await response.json();
  } catch (error) {
    console.error('Add image error:', error);
    throw error;
  }
};

export const updateGalleryImage = async (imageId, updates) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/gallery/${imageId}`, {
      method: 'PATCH',
      headers: authHeaders(),
      body: JSON.stringify(updates)
    });
    if (!response.ok) {
      throw new Error('Failed to update image');
    }
    return await response.json();
  } catch (error) {
    console.error('Update image error:', error);
    throw error;
  }
};

export const deleteGalleryImage = async (imageId) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/gallery/${imageId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to delete image');
    }
    return await response.json();
  } catch (error) {
    console.error('Delete image error:', error);
    throw error;
  }
};

// ========================================
// SITE CONTENT MANAGEMENT
// ========================================

export const getServicesContent = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/content/services`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch services');
    }
    return await response.json();
  } catch (error) {
    console.error('Services error:', error);
    throw error;
  }
};

export const updateServicesContent = async (services) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/content/services`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ content: services })
    });
    if (!response.ok) {
      throw new Error('Failed to update services');
    }
    return await response.json();
  } catch (error) {
    console.error('Update services error:', error);
    throw error;
  }
};

export const getCommercialShowcase = async () => {
  try {
    const response = await fetch(`${API_URL}/api/admin/content/commercial-showcase`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch commercial showcase');
    }
    return await response.json();
  } catch (error) {
    console.error('Commercial showcase error:', error);
    throw error;
  }
};

export const updateCommercialShowcase = async (showcase) => {
  try {
    const response = await fetch(`${API_URL}/api/admin/content/commercial-showcase`, {
      method: 'PUT',
      headers: authHeaders(),
      body: JSON.stringify({ content: showcase })
    });
    if (!response.ok) {
      throw new Error('Failed to update commercial showcase');
    }
    return await response.json();
  } catch (error) {
    console.error('Update commercial showcase error:', error);
    throw error;
  }
};

// ========================================
// USER MANAGEMENT
// ========================================

export const resetUserPassword = async (userId, newPassword) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/reset-user-password`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ user_id: userId, new_password: newPassword })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to reset password');
    }
    return await response.json();
  } catch (error) {
    console.error('Reset password error:', error);
    throw error;
  }
};

export const getAllUsers = async () => {
  try {
    const response = await fetch(`${API_URL}/api/auth/users`, {
      headers: authHeaders()
    });
    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }
    return await response.json();
  } catch (error) {
    console.error('Get users error:', error);
    throw error;
  }
};

export const createUser = async (email, password, full_name) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/register`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify({ email, password, full_name })
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to create user');
    }
    return await response.json();
  } catch (error) {
    console.error('Create user error:', error);
    throw error;
  }
};

export const deleteUser = async (userId) => {
  try {
    const response = await fetch(`${API_URL}/api/auth/users/${userId}`, {
      method: 'DELETE',
      headers: authHeaders()
    });
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Failed to delete user');
    }
    return await response.json();
  } catch (error) {
    console.error('Delete user error:', error);
    throw error;
  }
};
